import "./FormBuilderWeb.scss";

import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import classNames from "classnames";
import React from "react";

import FormBuilderCore from "./Core/FormBuilderCore";
import FormsyCheckbox from "./FormsyMaterialUI/FormsyCheckbox";
import FormsyComposedNewCheckbox from "./FormsyMaterialUI/FormsyComposedNewCheckbox";
import FormsyComposedNewCheckboxGroup from "./FormsyMaterialUI/FormsyComposedNewCheckboxGroup";
import FormsyComposedTextareaField from "./FormsyMaterialUI/FormsyComposedTextareaField";
import FormsyComposedTextField from "./FormsyMaterialUI/FormsyComposedTextField";
import FormsyComposedNewTextField from "./FormsyMaterialUI/FormsyComposedNewTextField";
import FormsyDatePicker from "./FormsyMaterialUI/FormsyDatePicker";
import FormsyRadioGroup from "./FormsyMaterialUI/FormsyRadioGroup";
import FormsySelect from "./FormsyMaterialUI/FormsySelect";
import FormsyComposedPasswordField from "./FormsyMaterialUI/FormsyComposedPassword";
class FormBuilderWeb extends FormBuilderCore {
  getField(field, key, fields) {
    const {
      name,
      label,
      value,
      type,
      options,
      selected,
      wrapperClassName,
      nativeStyles: _nativeStyles,
      labelStyle: _labelStyle,
      labelTagsStyles: _labelTagsStyles,
      ...rest
    } = field;

    const validationProps = this.getValidationProps(field);

    switch (type) {
      case "select":
        return (
          <div key={key} className={wrapperClassName}>
            <FormsySelect
              name={name}
              label={label}
              value={value}
              options={options}
              {...validationProps}
              onChange={value => this.handleChange(field, value)}
              {...rest}
            />
          </div>
        );

      case "checkbox":
        return (
          <div key={key} className={wrapperClassName}>
            {options.map(({ label: optionLabel, value, ...option }, index) => (
              <FormsyCheckbox
                key={index}
                label={optionLabel}
                name={name}
                value={selected.indexOf(value) !== -1}
                {...option}
                {...validationProps}
                onChange={() => this.handleCheckboxChange(field, value)}
                {...rest}
              />
            ))}
          </div>
        );

      case "checkbox-group": {
        const { fields: controlledFields } = rest;

        const isActive = this.getIsCheckboxGroupActive(controlledFields, fields);

        return (
          <div key={key} className={wrapperClassName}>
            <FormsyCheckbox
              label={label}
              name={name}
              value={isActive}
              {...validationProps}
              onChange={() =>
                this.handleGroupCheckboxChange(
                  controlledFields,
                  fields,
                  !isActive
                )
              }
              {...rest}
            />
          </div>
        );
      }

      case "newCheckbox":
        return (
          <div key={key} className={wrapperClassName}>
            {options.map(
              (
                { label: optionLabel, value: optionValue, ...option },
                index
              ) => (
                <FormsyComposedNewCheckbox
                  key={index}
                  label={optionLabel}
                  name={name}
                  value={selected.indexOf(optionValue) !== -1}
                  {...option}
                  {...validationProps}
                  onChange={() => this.handleCheckboxChange(field, optionValue)}
                  {...rest}
                />
              )
            )}
          </div>
        );

      case "newCheckbox-group": {
        const { fields: controlledCheckboxFields, ...groupRest } = rest;
        const isGroupActive = this.getIsCheckboxGroupActive(
          controlledCheckboxFields,
          fields
        );
        const hasSomeSelected = controlledCheckboxFields.some(fieldName => {
          const fieldConfig = fields.find(x => x.name === fieldName);
          return fieldConfig?.selected?.length > 0;
        });

        return (
          <div key={key} className={wrapperClassName}>
            <FormsyComposedNewCheckboxGroup
              label={label}
              value={isGroupActive}
              indeterminate={hasSomeSelected && !isGroupActive}
              onGroupChange={() =>
                this.handleGroupCheckboxChange(
                  controlledCheckboxFields,
                  fields,
                  !isGroupActive
                )
              }
              {...groupRest}
            />
          </div>
        );
      }

      case "radio":
        return (
          <div key={key} className={wrapperClassName}>
            <FormsyRadioGroup
              aria-label={label}
              name={name}
              value={value}
              {...validationProps}
              onChange={value => this.handleChange(field, value)}
              {...rest}
            >
              {options.map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                />
              ))}
            </FormsyRadioGroup>
          </div>
        );

      case "date":
        return (
          <div key={key} className={wrapperClassName}>
            <FormsyDatePicker
              id={name}
              name={name}
              value={value}
              label={label}
              {...validationProps}
              onChange={value => this.handleChange(field, value)}
              {...rest}
            />
          </div>
        );
      case "textarea": {
        return (
          <div key={key} className={wrapperClassName}>
            <FormsyComposedTextareaField
              id={name}
              name={name}
              value={value}
              label={label}
              {...validationProps}
              onChange={value => this.handleChange(field, value)}
              {...rest}
            />
          </div>
        );
      }
      case "password":
        return (
          <div key={key} className={wrapperClassName}>
            <FormsyComposedPasswordField
              id={name}
              name={name}
              value={value}
              label={label}
              {...validationProps}
              onChange={value => this.handleChange(field, value)}
              {...rest}
            />
          </div>
        );
      case "newText": {
        const { inputType, ...newTextRest } = rest;
        return (
          <div key={key} className={wrapperClassName}>
            <FormsyComposedNewTextField
              id={name}
              name={name}
              value={value}
              label={label}
              inputType={inputType}
              {...validationProps}
              onChange={value => this.handleChange(field, value)}
              {...newTextRest}
            />
          </div>
        );
      }
      case "email":
      case "text":
      default:
        return (
          <div key={key} className={wrapperClassName}>
            <FormsyComposedTextField
              type={type}
              id={name}
              name={name}
              value={value}
              label={label}
              {...validationProps}
              onChange={value => this.handleChange(field, value)}
              {...rest}
            />
          </div>
        );
    }
  }

  render() {
    const { fields, style, className, validationErrors } = this.props;

    const nonFieldErrors =
      validationErrors?.non_field_errors || validationErrors?.__all__;

    return (
      <div
        className={classNames({
          "form-builder": true,
          [className]: !!className
        })}
        style={style}
      >
        {fields
          ? fields.map((field, index) => this.getField(field, index, fields))
          : "Missing `fields` prop"}
        {nonFieldErrors ? (
          <p className={"error-message"}>{nonFieldErrors}</p>
        ) : null}
      </div>
    );
  }
}

export default FormBuilderWeb;
